/**
 * Auth redirect utility for handling navigation from outside React components
 *
 * This module provides a way to trigger navigation to the login page from
 * places like Apollo error links that don't have access to React hooks.
 *
 * Usage:
 * - In non-React code: call triggerAuthRedirect()
 * - In the app: use useAuthRedirectListener() hook to listen and navigate
 */

// Custom event name for auth redirects
const AUTH_REDIRECT_EVENT = "auth:redirect";

export interface AuthRedirectEvent {
  to: string;
  reason?: string;
}

/**
 * Trigger an auth redirect from anywhere in the app
 * This dispatches a custom event that the app can listen to
 */
export function triggerAuthRedirect(to: string, reason?: string): void {
  const event = new CustomEvent<AuthRedirectEvent>(AUTH_REDIRECT_EVENT, {
    detail: { to, reason },
  });
  window.dispatchEvent(event);
}

/**
 * Subscribe to auth redirect events
 * Returns an unsubscribe function
 */
export function subscribeToAuthRedirect(
  callback: (event: AuthRedirectEvent) => void,
): () => void {
  const handler = (e: Event) => {
    const customEvent = e as CustomEvent<AuthRedirectEvent>;
    callback(customEvent.detail);
  };

  window.addEventListener(AUTH_REDIRECT_EVENT, handler);
  return () => window.removeEventListener(AUTH_REDIRECT_EVENT, handler);
}

/**
 * Build the decoupled login URL (auth.foir.io) with a guarded returnTo.
 *
 * `authBaseUrl` is the consuming app's VITE_AUTH_URL (e.g. https://auth.foir.io).
 * `returnTo` should be an absolute URL the auth app sends the browser back to
 * after login — defaults to the current location. auth.foir.dev re-validates
 * returnTo against its own allow-list, so this just passes it through.
 *
 * `provider` names the way in the person already chose on this page, such as
 * "Continue with Google": the login page starts it rather than asking again.
 *
 * `join` opens the login page on joining, for somebody who came to make an
 * account. Joining happens there for every pool: the code it sends proves the
 * address before anybody is made.
 */
export function buildLoginUrl(
  authBaseUrl: string | undefined,
  returnTo?: string,
  provider?: string,
  options?: { join?: boolean },
): string {
  if (!authBaseUrl) {
    // No identity host configured — surface loudly rather than silently
    // bouncing to a now-removed local /login route.
    console.error(
      "[auth] VITE_AUTH_URL is not set; cannot redirect to the login surface.",
    );
  }
  const base = (authBaseUrl || "").replace(/\/+$/, "");
  const rt =
    returnTo ?? (typeof window !== "undefined" ? window.location.href : "");
  const params = new URLSearchParams();
  if (rt) params.set("returnTo", rt);
  if (provider) params.set("provider", provider.toLowerCase());
  if (options?.join) params.set("prompt", "create");
  const qs = params.toString();
  return `${base}/login${qs ? `?${qs}` : ""}`;
}

/**
 * Redirect the browser to the decoupled login surface (auth.foir.dev).
 * User apps call this from their AuthGate / 401 handler instead of
 * navigating to a local /login route.
 */
export function redirectToLogin(
  authBaseUrl: string | undefined,
  returnTo?: string,
): void {
  if (typeof window === "undefined") return;
  window.location.href = buildLoginUrl(authBaseUrl, returnTo);
}

/**
 * Build the signup URL with optional search params (e.g. planSlug, returnTo).
 * The account console's /signup (e.g. account.foir.io/signup) sends somebody
 * signed out to the login page to join, and walks somebody signed in through
 * founding a workspace. Callers pass their platform/console base URL
 * (config.accountUrl / VITE_ACCOUNT_URL), not authUrl.
 */
export function buildSignupUrl(
  consoleBaseUrl: string | undefined,
  params?: Record<string, string | undefined>,
): string {
  if (!consoleBaseUrl) {
    console.error(
      "[auth] console URL is not set; cannot redirect to signup.",
    );
  }
  const base = (consoleBaseUrl || "").replace(/\/+$/, "");
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v) search.set(k, v);
  }
  const qs = search.toString();
  return `${base}/signup${qs ? `?${qs}` : ""}`;
}

/** Redirect the browser to the account console's signup surface (e.g. account.foir.io/signup). */
export function redirectToSignup(
  consoleBaseUrl: string | undefined,
  params?: Record<string, string | undefined>,
): void {
  if (typeof window === "undefined") return;
  window.location.href = buildSignupUrl(consoleBaseUrl, params);
}

/**
 * Build the account app's workspace-selection URL with a guarded returnTo.
 * `platformBaseUrl` is the consuming app's VITE_PLATFORM_URL. The account app
 * sends the browser back to returnTo once a workspace/project context exists.
 *
 * Selection happens on the account HOME, not at `/workspaces`. That path holds
 * `create` and `$tenantId` and never had an index: the account app carries no
 * cross-tenant list (that table lives in the staff console), so the home — which
 * already lists your workspaces, one row and one click each — IS the selection
 * surface. Pointing here at a route that was never built is what sent an
 * user with no project to a 404 on the way to picking one.
 */
export function buildWorkspaceSelectUrl(
  platformBaseUrl: string | undefined,
  returnTo?: string,
): string {
  if (!platformBaseUrl) {
    console.error(
      "[auth] VITE_PLATFORM_URL is not set; cannot redirect to workspace selection.",
    );
  }
  const base = (platformBaseUrl || "").replace(/\/+$/, "");
  const rt =
    returnTo ?? (typeof window !== "undefined" ? window.location.href : "");
  const qs = rt ? `?returnTo=${encodeURIComponent(rt)}` : "";
  return `${base}/${qs}`;
}

/**
 * Redirect the browser to the platform app to pick a workspace/project. User
 * apps (e.g. the console) call this when they have no project context instead of
 * hosting their own selection page — workspace selection is a control-plane
 * concern that lives in the platform app.
 */
export function redirectToWorkspaceSelect(
  platformBaseUrl: string | undefined,
  returnTo?: string,
): void {
  if (typeof window === "undefined") return;
  window.location.href = buildWorkspaceSelectUrl(platformBaseUrl, returnTo);
}

/**
 * Build a link to one workspace in the account console, falling back to the
 * account home when the caller has no tenant selected — the home is where
 * workspaces are listed and picked. The account app is URL-scoped, so this
 * direction of the handoff needs no extra machinery — it just needs the id,
 * which the caller's session already holds.
 */
export function buildWorkspaceUrl(
  platformBaseUrl: string | undefined,
  tenantId?: string | null,
): string {
  const base = (platformBaseUrl || "").replace(/\/+$/, "");
  return tenantId ? `${base}/workspaces/${tenantId}` : `${base}/`;
}

/**
 * The query params that carry session context INTO the console.
 *
 * The console is session-scoped: its URLs hold no tenant/project ids, and context is
 * whatever the session's last switchTenant/switchProject left behind. That is
 * fine inside the app and useless across an origin boundary — a link from the
 * platform console (or an agent, or a docs console-link) naming a project has
 * no way to say WHICH project without them. These two params are that way in:
 * the console applies them to the session on arrival and then strips them, so they
 * are a one-shot instruction, never part of the console's addressable URL space.
 *
 * Deliberately short and unprefixed because they appear in links a human may
 * read. Kept here, next to the builder, so the emitting and consuming sides
 * cannot drift onto different spellings.
 */
export const CONSOLE_TENANT_PARAM = "tenant";
export const CONSOLE_PROJECT_PARAM = "project";

export interface ConsoleUrlOptions {
  /** Workspace to select on arrival. Derived from the project when omitted. */
  tenantId?: string | null;
  /** Project to select on arrival. */
  projectId?: string | null;
  /** App-relative path to land on, e.g. "/models". Defaults to the home page. */
  path?: string;
}

/**
 * Build a link into the console that carries the workspace/project it is
 * about. `consoleBaseUrl` is the consuming app's VITE_CONSOLE_URL.
 *
 * Without this, every "open this project" link in the platform console resolves
 * to whatever project the reader's console session happened to be on last, which
 * is right about as often as they have one project.
 *
 * When both ids are known the link is PATH-scoped: `/t/{tenant}/p/{project}{screen}`.
 * The console resolves scope from the path (no session mutation, so two tabs on
 * two projects stay independent) and mints the audited workspace-entry grant for a
 * staff drill-in on arrival. Only when an id is missing does it fall back to the
 * one-shot `?tenant=&project=` params, which the console still applies to the
 * session on arrival, so an incomplete or legacy link keeps resolving.
 */
export function buildConsoleUrl(
  consoleBaseUrl: string | undefined,
  options: ConsoleUrlOptions = {},
): string {
  if (!consoleBaseUrl) {
    console.error(
      "[auth] VITE_CONSOLE_URL is not set; cannot link into the console.",
    );
  }
  const base = (consoleBaseUrl || "").replace(/\/+$/, "");
  const path = options.path ?? "/";
  const screen = path.startsWith("/") ? path : `/${path}`;

  if (options.tenantId && options.projectId) {
    const prefix = `/t/${encodeURIComponent(options.tenantId)}/p/${encodeURIComponent(options.projectId)}`;
    // The home screen is the scope root itself, so it carries no trailing path.
    return `${base}${prefix}${screen === "/" ? "" : screen}`;
  }

  const search = new URLSearchParams();
  if (options.tenantId) search.set(CONSOLE_TENANT_PARAM, options.tenantId);
  if (options.projectId) search.set(CONSOLE_PROJECT_PARAM, options.projectId);
  const qs = search.toString();
  return `${base}${screen}${qs ? `?${qs}` : ""}`;
}
